import { TranslateService } from '@ngx-translate/core';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { OptionStateWithCount } from '../../models';
import { debounceTime, map, shareReplay } from 'rxjs/operators';

import { DataFilterManagerService } from '../../services';

@Component({
  selector: 'app-filter-dropdown',
  templateUrl: './filter-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FilterDropdownComponent implements OnInit {
  /** Filter instance id to link this dropdown to */
  @Input()
  filterInstanceId: string;

  /** Filter field id to link this dropdown to in the given filter instance */
  @Input()
  filterFieldId: string;

  /** Entity name to be displayed next to counter. If not specified, only counter will be displayed */
  @Input()
  entityNamePlural: string;

  @Input()
  customSortCallback: (item1: any, item2: any) => number;

  @Input()
  visibleItemsCount: number;

  options$: Observable<OptionStateWithCount[]>;
  selectedOption$: Observable<OptionStateWithCount>;
  displayValueSelector = this.selectDispayValue.bind(this);

  constructor(
    private dataFilterManagerService: DataFilterManagerService,
    private translationService: TranslateService
  ) {}

  ngOnInit(): void {
    this.options$ = this.dataFilterManagerService
      .getOptionsWithCountForFieldSelector(this.filterInstanceId, this.filterFieldId)
      .pipe(
        map((options) => {
          return options.filter((opt) => opt.count || opt.showOnZeroCount);
        }),
        map((options) => {
          if (this.customSortCallback) {
            options.sort((option1, option2) => this.customSortCallback(option1.value, option2.value));
          }

          return options;
        }),
        debounceTime(100),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    this.selectedOption$ = this.options$.pipe(map((options) => options.find((option) => option.isChecked)));
  }

  disableOnCountZero(optionState: OptionStateWithCount): boolean {
    return !optionState.count;
  }

  selectOption(optionState: OptionStateWithCount): void {
    this.dataFilterManagerService.selectOptions(this.filterInstanceId, { [this.filterFieldId]: [optionState.id] });
  }

  private selectDispayValue(optionState: OptionStateWithCount): string {
    let countTerm = `${optionState.count}`;

    if (this.entityNamePlural) {
      countTerm = `${optionState.count} ${this.entityNamePlural}`;
    }

    let displayValue = optionState.value;
    if (optionState.translationKey) {
      displayValue = this.translationService.instant(optionState.translationKey);
    }

    return `${displayValue} (${countTerm})`;
  }
}
